import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LegalSidePanel from '@/components/LegalSidePanel';

const SectionHeading = ({ number, title }) => (
  <h2 className="text-lg font-bold mb-3 mt-8 first:mt-0" style={{ color: '#131B2E' }}>
    {number}. {title}
  </h2>
);

const Paragraph = ({ children }) => (
  <p className="text-sm leading-relaxed mb-3" style={{ color: '#475569' }}>
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 mb-3 pl-4">
    {items.map((item, i) => (
      <li key={i} className="text-sm leading-relaxed flex items-start gap-2" style={{ color: '#475569' }}>
        <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: '#0A3D91' }} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function TermsOfServicePage({ onOpenContactModal }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="terms-of-service-page" style={{ backgroundColor: '#FFFFFF' }}>
      <Helmet>
        <title>Terms of Service | KritvaSoft</title>
        <meta name="description" content="KritvaSoft Terms of Service — terms and conditions governing use of our services." />
        <link rel="canonical" href="https://www.kritvasoft.com/terms-of-service" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium mb-6 transition-colors"
            style={{ color: '#475569' }}
            data-testid="terms-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Left Column — Legal Content */}
            <div className="lg:col-span-8">

          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight mb-1"
            style={{ color: '#131B2E' }}
            data-testid="terms-headline"
          >
            Terms of Service
          </h1>
          <p className="text-sm mb-3" style={{ color: '#475569' }}>
            Effective Date: March 2026
          </p>
          <div className="w-12 h-0.5 mb-6" style={{ backgroundColor: '#0A3D91' }} />

          <Paragraph>
            These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the KritvaSoft website and services. By using our website, you agree to be bound by these Terms. If you do not agree, please discontinue use immediately.
          </Paragraph>

          <div className="space-y-0" data-testid="terms-content">
            {/* Section 1 */}
            <SectionHeading number={1} title="Acceptance of Terms" />
            <Paragraph>
              By accessing or using this website, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. These Terms apply to all visitors, users, and clients of KritvaSoft.
            </Paragraph>

            {/* Section 2 */}
            <SectionHeading number={2} title="Use of Website" />
            <Paragraph>You agree not to:</Paragraph>
            <BulletList items={[
              'Violate any applicable local, national, or international law or regulation',
              'Attempt unauthorized access to any part of the website, server, or connected systems',
              'Disrupt, interfere with, or compromise the integrity or performance of our systems',
              'Copy, reproduce, or redistribute any content without prior written consent',
              'Use the website for any unlawful or fraudulent purpose',
            ]} />

            {/* Section 3 */}
            <SectionHeading number={3} title="Services" />
            <Paragraph>KritvaSoft provides professional technology services, including:</Paragraph>
            <BulletList items={[
              'Custom software development and engineering',
              'AI-powered automation and intelligent systems',
              'SaaS platform design, development, and scaling',
              'Digital growth strategy and implementation',
            ]} />
            <Paragraph>
              The specific scope, deliverables, timeline, and pricing for each engagement are defined in individual project agreements between KritvaSoft and the client. These Terms supplement but do not override project-specific agreements.
            </Paragraph>

            {/* Section 4 */}
            <SectionHeading number={4} title="Intellectual Property" />
            <Paragraph>
              All content on this website &mdash; including text, graphics, logos, design elements, and code &mdash; is the intellectual property of KritvaSoft and is protected by applicable copyright and trademark laws, unless otherwise stated.
            </Paragraph>
            <Paragraph>
              Intellectual property created during client engagements is governed by the terms of the respective project agreement. Unless otherwise specified, all IP is transferred to the client upon full payment.
            </Paragraph>

            {/* Section 5 */}
            <SectionHeading number={5} title="Disclaimer of Warranties" />
            <Paragraph>
              This website and its content are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis without warranties of any kind, whether express or implied. KritvaSoft does not guarantee uninterrupted access, error-free operation, or the accuracy of information presented.
            </Paragraph>

            {/* Section 6 */}
            <SectionHeading number={6} title="Limitation of Liability" />
            <Paragraph>
              To the maximum extent permitted by law, KritvaSoft shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use our website or services. Our total liability for any claim shall not exceed the total amount paid by you for the specific engagement giving rise to the claim.
            </Paragraph>

            {/* Section 7 */}
            <SectionHeading number={7} title="Third-Party Links" />
            <Paragraph>
              Our website may contain links to third-party websites or services. KritvaSoft is not responsible for the content, accuracy, privacy policies, or practices of any third-party sites. Accessing such links is at your own risk.
            </Paragraph>

            {/* Section 8 */}
            <SectionHeading number={8} title="User Submissions" />
            <Paragraph>
              Any information, data, or materials you submit through our website &mdash; including contact forms, project inquiries, and consultation requests &mdash; must be accurate, lawful, and non-infringing. You are solely responsible for the content you provide.
            </Paragraph>

            {/* Section 9 */}
            <SectionHeading number={9} title="Governing Law" />
            <Paragraph>
              These Terms shall be governed by and construed in accordance with the applicable laws of the jurisdiction in which KritvaSoft operates. Any disputes arising from these Terms shall be resolved through good-faith negotiation, and if necessary, through binding arbitration or the appropriate courts.
            </Paragraph>

            {/* Section 10 */}
            <SectionHeading number={10} title="Updates to These Terms" />
            <Paragraph>
              We reserve the right to modify or update these Terms at any time. Changes will be posted on this page with a revised effective date. Your continued use of the website after changes are posted constitutes acceptance of the updated Terms.
            </Paragraph>

            {/* Section 11 */}
            <SectionHeading number={11} title="Contact" />
            <Paragraph>
              If you have questions about these Terms of Service, please contact us:
            </Paragraph>
            <div
              className="mt-3 p-4 rounded-lg border"
              style={{ borderColor: '#E2E8F0', backgroundColor: 'rgba(10, 61, 145, 0.02)' }}
            >
              <p className="text-sm font-bold mb-1" style={{ color: '#131B2E' }}>KritvaSoft</p>
              <p className="text-sm" style={{ color: '#475569' }}>
                Email:{' '}
                <a href="mailto:legal@kritvasoft.com" className="font-medium" style={{ color: '#0A3D91' }}>
                  legal@kritvasoft.com
                </a>
              </p>
              <p className="text-sm" style={{ color: '#475569' }}>
                Website:{' '}
                <a href="https://kritvasoft.com" className="font-medium" style={{ color: '#0A3D91' }}>
                  kritvasoft.com
                </a>
              </p>
            </div>
          </div>
            </div>

            {/* Right Column — Sticky Panel */}
            <div className="lg:col-span-4">
              <LegalSidePanel onOpenContactModal={onOpenContactModal} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
